import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReportPeriodService from '../shared/services/report-period/report-period.service';
import { UserDetailsContext } from '../context/user-details/user-details';
import { userHasRequiredRole } from '../shared/utils/role-utils';

interface JustAuthenticatedState {
  justAuthenticated?: boolean;
}

/**
 * Decides whether to show the first-run Welcome dialog on the dashboard.
 *
 * Three conditions, all required:
 *  1. The user has *just* authenticated — signalled by React Router navigation
 *     state (`{ justAuthenticated: true }`) set on the post-auth redirect. This
 *     does not survive a hard refresh, and we consume it immediately, so the
 *     dialog never reappears on refresh or back-navigation.
 *  2. The user is an admin. The dialog's steps and its setup-guide CTA all lead
 *     to admin-gated surfaces (the dashboard Get-started guide, settings,
 *     organisation pages), so for anyone else every path is a dead end. An
 *     invited non-admin gets the dashboard orientation banner instead.
 *  3. The org has no reporting period yet. Once a period exists it never shows
 *     again — this is the permanent off-switch, so no persisted "seen" flag is
 *     needed.
 *
 * The period check is a FRESH fetch, deliberately not `ReportPeriodContext`. The
 * shared context's `reportPeriodsLoaded` flag is sticky across an auth change: it
 * is already `true` (with an empty list) on the logged-out `/login` screen and
 * stays `true` while the context re-fetches for the newly logged-in user, so
 * reading it would momentarily see "loaded + zero periods" and wrongly show the
 * dialog to a returning user who already has a period.
 */
export const useShowWelcomeDialog = () => {
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const userDetailsContext = useContext(UserDetailsContext);
  const [open, setOpen] = useState(false);
  // Guard so the one-shot trigger runs a single time (incl. StrictMode remount).
  const evaluated = useRef(false);

  useEffect(() => {
    if (evaluated.current) return;
    if (!(state as JustAuthenticatedState | null)?.justAuthenticated) return;
    // The post-auth redirect happens after the session is loaded, so the user
    // is normally present already; if not, wait rather than consume the signal
    // before the role is known.
    const userDetails = userDetailsContext.userDetails;
    if (!userDetails) return;
    evaluated.current = true;

    // Consume the signal so a refresh / back-navigation can't retrigger it.
    navigate(pathname, { replace: true, state: {} });

    if (!userHasRequiredRole(userDetails, ['admin'])) return;

    // Fresh, current-user fetch — show only when the org has no period yet.
    const reportPeriodService = new ReportPeriodService();
    reportPeriodService
      .getAllReportPeriod()
      .then((periods) => {
        if (periods.length === 0) setOpen(true);
      })
      .catch((err) => {
        // On error, fail closed (don't show) rather than risk a wrong trigger.
        console.log(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetailsContext.userDetails]);

  return { open, close: () => setOpen(false) };
};
